import { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import {
  DEMO_EXAMPLES,
  DEMO_MEDIA,
  type DemoExample,
  type DemoPreview,
} from './demo-media.js';

export type DemoMedia = ReturnType<typeof useDemoMedia>;

export const formatTime = (seconds: number): string => {
  const safe = Number.isFinite(seconds) ? Math.max(0, seconds) : 0;
  return `${String(Math.floor(safe / 60))}:${String(Math.floor(safe % 60)).padStart(2, '0')}`;
};

function load(video: HTMLVideoElement, url: string) {
  if (video.getAttribute('src') === url) return;
  video.src = url;
  video.load();
}

// Re-read after await: an interaction can abort playback while play() is pending.
const cancelled = (controller: AbortController) => controller.signal.aborted;
const interrupted = (error: unknown) =>
  typeof error === 'object' &&
  error !== null &&
  'name' in error &&
  error.name === 'AbortError';

function ready(video: HTMLVideoElement, signal: AbortSignal): Promise<void> {
  if (signal.aborted) return Promise.reject(new Error('cancelled'));
  if (video.readyState >= 1) return Promise.resolve();
  return new Promise((resolve, reject) => {
    const finish = (error?: Error) => {
      clearTimeout(timeout);
      video.removeEventListener('loadedmetadata', success);
      video.removeEventListener('error', failure);
      signal.removeEventListener('abort', cancel);
      if (error) reject(error);
      else resolve();
    };
    const success = () => {
      finish();
    };
    const failure = () => {
      finish(new Error('media unavailable'));
    };
    const cancel = () => {
      finish(new Error('cancelled'));
    };
    const timeout = setTimeout(() => {
      finish(new Error('media timeout'));
    }, 15000);
    video.addEventListener('loadedmetadata', success);
    video.addEventListener('error', failure);
    signal.addEventListener('abort', cancel, { once: true });
  });
}

/** Playback follows media events. The prepared journey never calls a generation API. */
export function useDemoMedia(step: number, suspended: boolean, example: DemoExample) {
  const pair = DEMO_EXAMPLES[example];
  const source = useRef<HTMLVideoElement>(null);
  const magic = useRef<HTMLVideoElement>(null);
  const travel = useRef<HTMLVideoElement>(null);
  const before = useRef<HTMLVideoElement>(null);
  const after = useRef<HTMLVideoElement>(null);
  const refs = useMemo(() => ({ source, magic, travel, before, after }), []);
  const operation = useRef<AbortController | null>(null);
  const wanted = useRef(false);
  const [playing, setPlaying] = useState(false);
  const [previewPlaying, setPreviewPlaying] = useState<DemoPreview | null>(null);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState(false);
  const [muted, setMuted] = useState(true);
  const [current, setCurrent] = useState(0);
  const [duration, setDuration] = useState(0);

  const sync = useCallback(() => {
    setPreviewPlaying(
      (['source', 'magic', 'travel'] as const).find((key) => {
        const video = refs[key].current;
        return video !== null && !video.paused && !video.ended;
      }) ?? null,
    );
    const first = before.current,
      second = after.current;
    if (!first || !second) return;
    const length = Math.min(first.duration || Infinity, second.duration || Infinity);
    setDuration(Number.isFinite(length) ? length : 0);
    setCurrent(second.currentTime);
    setPlaying(!first.paused && !second.paused);
    if (!second.paused && Math.abs(first.currentTime - second.currentTime) > 0.18) {
      first.currentTime = second.currentTime;
    }
    if (Number.isFinite(length) && second.currentTime >= length - 0.04) {
      wanted.current = false;
      if (!first.paused) first.pause();
      if (!second.paused) second.pause();
      setPlaying(false);
    }
  }, [refs]);

  const pause = useCallback(() => {
    wanted.current = false;
    operation.current?.abort();
    Object.values(refs).forEach((ref) => {
      if (ref.current?.getAttribute('src')) ref.current.pause();
    });
    setPlaying(false);
    setPreviewPlaying(null);
    setLoading(false);
  }, [refs]);
  const fail = useCallback(() => {
    pause();
    setError(true);
  }, [pause]);
  const begin = useCallback(() => {
    operation.current?.abort();
    const controller = new AbortController();
    operation.current = controller;
    setError(false);
    return controller;
  }, []);

  useEffect(() => {
    pause();
    if (step !== 4 || suspended) return;
    const first = before.current,
      second = after.current;
    if (!first || !second) return;
    const controller = begin();
    load(first, pair.source);
    load(second, pair.result);
    void Promise.all([
      ready(first, controller.signal),
      ready(second, controller.signal),
    ])
      .then(sync)
      .catch(() => {
        if (!controller.signal.aborted) setError(true);
      });
    return () => {
      controller.abort();
      first.pause();
      second.pause();
    };
  }, [step, suspended, pause, begin, sync, pair]);

  useEffect(
    () => () => {
      wanted.current = false;
      operation.current?.abort();
    },
    [],
  );

  const preview = async (key: DemoPreview) => {
    const video = refs[key].current;
    if (!video) return;
    const wasPlaying = !video.paused;
    pause();
    if (wasPlaying) return;
    const controller = begin();
    load(video, DEMO_MEDIA[key]);
    if (video.error) video.load();
    try {
      await ready(video, controller.signal);
      if (cancelled(controller)) return;
      if (video.ended) video.currentTime = 0;
      await video.play();
      if (cancelled(controller)) video.pause();
    } catch {
      if (!controller.signal.aborted) fail();
    }
    sync();
  };

  const toggle = async (restart = false) => {
    const first = before.current,
      second = after.current;
    if (!first || !second) return;
    if ((wanted.current || playing) && !restart) {
      pause();
      return;
    }
    pause();
    const controller = begin();
    wanted.current = true;
    setLoading(true);
    load(first, pair.source);
    load(second, pair.result);
    if (first.error) first.load();
    if (second.error) second.load();
    try {
      await Promise.all([
        ready(first, controller.signal),
        ready(second, controller.signal),
      ]);
      if (cancelled(controller)) return;
      const length = Math.min(first.duration, second.duration);
      const position =
        restart || second.currentTime >= length - 0.05 ? 0 : second.currentTime;
      if (first.currentTime !== position) first.currentTime = position;
      if (second.currentTime !== position) second.currentTime = position;
      await Promise.all([first.play(), second.play()]);
      if (cancelled(controller)) {
        first.pause();
        second.pause();
        return;
      }
    } catch (error) {
      // Buffering can pause a pending play(). canplay resumes the same intent.
      if (!controller.signal.aborted && !interrupted(error)) fail();
    } finally {
      if (!controller.signal.aborted) {
        // Media events can clear this ref while play() is awaiting a buffer.
        // eslint-disable-next-line @typescript-eslint/no-unnecessary-condition
        setLoading(wanted.current && (first.paused || second.paused));
        sync();
      }
    }
  };

  // Pause both halves when either buffers; resume only when both can play.
  useEffect(() => {
    const first = before.current,
      second = after.current;
    if (!first || !second) return;
    const waiting = () => {
      if (!wanted.current) return;
      first.pause();
      second.pause();
      setLoading(true);
      setPlaying(false);
    };
    const resume = () => {
      if (!wanted.current || first.readyState < 3 || second.readyState < 3) return;
      if (!first.paused && !second.paused) return;
      if (Math.abs(first.currentTime - second.currentTime) > 0.18) {
        first.currentTime = second.currentTime;
      }
      const token = operation.current;
      void Promise.all([first.play(), second.play()])
        .then(() => {
          if (token?.signal.aborted || !wanted.current) {
            first.pause();
            second.pause();
            return;
          }
          setLoading(false);
          sync();
        })
        .catch((error: unknown) => {
          if (!token?.signal.aborted && !interrupted(error)) fail();
        });
    };
    [first, second].forEach((video) => {
      video.addEventListener('waiting', waiting);
      video.addEventListener('canplay', resume);
    });
    return () => {
      [first, second].forEach((video) => {
        video.removeEventListener('waiting', waiting);
        video.removeEventListener('canplay', resume);
      });
    };
  }, [fail, sync]);

  const seek = (percent: number) => {
    const first = before.current,
      second = after.current;
    if (!first || !second || duration <= 0) return;
    const position = (duration * Math.max(0, Math.min(100, percent))) / 100;
    first.currentTime = position;
    second.currentTime = position;
    sync();
  };
  const toggleSound = () => {
    if (!after.current) return;
    after.current.muted = !after.current.muted;
    setMuted(after.current.muted);
  };
  const reset = () => {
    pause();
    setError(false);
    setCurrent(0);
    setMuted(true);
    setDuration(0);
    Object.values(refs).forEach((ref) => {
      const video = ref.current;
      if (video?.getAttribute('src')) {
        video.removeAttribute('src');
        video.load();
      }
      if (video) video.muted = true;
    });
  };
  return {
    refs,
    playing,
    previewPlaying,
    loading,
    error,
    muted,
    current,
    duration,
    percent: duration > 0 ? (current / duration) * 100 : 0,
    sync,
    pause,
    fail,
    preview,
    toggle,
    seek,
    toggleSound,
    reset,
  };
}
